import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { ModuleQuestionType } from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSaveModuleQuestionsMutation } from "Modules/Api/Mutations/useSaveModuleQuestionsMutation";
import type { ModuleQuestionDto } from "Modules/Api/Queries/useModuleQuestionsQuery";
import { BlModal } from "Shared/Dialogs/BlModal";

interface OptionFormItem {
  _key: number;
  text: string;
  sortOrder: number;
}

interface QuestionFormItem {
  _key: number;
  text: string;
  type: ModuleQuestionType;
  isRequired: boolean;
  sortOrder: number;
  options: OptionFormItem[];
}

interface QuestionErrors {
  text?: string;
  options?: string;
  optionTexts?: Record<number, string>;
}

interface EditModuleQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleID: string;
  existingQuestions: ModuleQuestionDto[];
}

export const EditModuleQuestionsModal = ({
  isOpen,
  onClose,
  moduleID,
  existingQuestions,
}: EditModuleQuestionsModalProps): React.ReactNode => {
  const { t } = useResource();
  const mutation = useSaveModuleQuestionsMutation(moduleID);
  const nextKeyRef = useRef(1);
  const genKey = useCallback(() => nextKeyRef.current++, []);

  const [questions, setQuestions] = useState<QuestionFormItem[]>([]);
  const [errors, setErrors] = useState<Record<number, QuestionErrors>>({});
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    // Only reset form when modal opens (not on existingQuestions refetch while open)
    if (isOpen && !prevIsOpenRef.current) {
      setErrors({});
      setQuestions(
        existingQuestions.map((q, idx) => ({
          _key: genKey(),
          text: q.text,
          type: q.type,
          isRequired: q.isRequired,
          sortOrder: idx,
          options: q.options.map((o, oidx) => ({
            _key: genKey(),
            text: o.text,
            sortOrder: oidx,
          })),
        })),
      );
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, existingQuestions, genKey]);

  const clearQuestionError = (key: number) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        _key: genKey(),
        text: "",
        type: ModuleQuestionType.Text,
        isRequired: false,
        sortOrder: prev.length,
        options: [],
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    const key = questions[index]._key;
    clearQuestionError(key);
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuestion = (
    index: number,
    field: keyof QuestionFormItem,
    value: unknown,
  ) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;
        const updated = { ...q, [field]: value };
        if (field === "type" && !isChoiceType(value as ModuleQuestionType)) {
          updated.options = [];
        }
        if (
          field === "type" &&
          isChoiceType(value as ModuleQuestionType) &&
          updated.options.length === 0
        ) {
          updated.options = [{ _key: genKey(), text: "", sortOrder: 0 }];
        }
        return updated;
      }),
    );
    clearQuestionError(questions[index]._key);
  };

  const addOption = (questionIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === questionIndex
          ? {
              ...q,
              options: [
                ...q.options,
                { _key: genKey(), text: "", sortOrder: q.options.length },
              ],
            }
          : q,
      ),
    );
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === questionIndex
          ? { ...q, options: q.options.filter((_, oi) => oi !== optionIndex) }
          : q,
      ),
    );
  };

  const updateOptionText = (
    questionIndex: number,
    optionIndex: number,
    text: string,
  ) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === questionIndex
          ? {
              ...q,
              options: q.options.map((o, oi) =>
                oi === optionIndex ? { ...o, text } : o,
              ),
            }
          : q,
      ),
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<number, QuestionErrors> = {};
    let isValid = true;

    for (const q of questions) {
      const qErrors: QuestionErrors = {};

      if (!q.text.trim()) {
        qErrors.text = t(
          Resources.Modules.Questions.ValidationQuestionTextRequired,
        );
        isValid = false;
      }

      if (isChoiceType(q.type)) {
        if (q.options.length === 0) {
          qErrors.options = t(
            Resources.Modules.Questions.ValidationOptionsRequired,
          );
          isValid = false;
        }

        const optionTexts: Record<number, string> = {};
        for (let i = 0; i < q.options.length; i++) {
          if (!q.options[i].text.trim()) {
            optionTexts[i] = t(
              Resources.Modules.Questions.ValidationOptionTextRequired,
            );
            isValid = false;
          }
        }
        if (Object.keys(optionTexts).length > 0) {
          qErrors.optionTexts = optionTexts;
        }
      }

      if (Object.keys(qErrors).length > 0) {
        newErrors[q._key] = qErrors;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      await mutation.mutateAsync({
        questions: questions.map((q, idx) => ({
          text: q.text,
          type: q.type,
          isRequired: q.isRequired,
          sortOrder: idx,
          options: q.options.map((o, oidx) => ({
            text: o.text,
            sortOrder: oidx,
          })),
        })),
      });
      toast.success(t(Resources.Modules.Questions.SaveSuccess));
      onClose();
    } catch {
      // error handled by mutation
    }
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Modules.Questions.EditQuestions)}
      onCancel={onClose}
      onConfirm={handleSave}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Common.Save)}
      isLoading={mutation.isPending}
      maxWidth="800px"
    >
      <Stack spacing={Spacing.M}>
        {questions.map((question, qIdx) => {
          const qErrors = errors[question._key];

          return (
            <Box
              key={question._key}
              sx={(theme) => ({
                border: `1px solid ${qErrors ? theme.palette.error.main : theme.colors.border}`,
                borderRadius: Radius.M,
                p: Spacing.S,
              })}
            >
              <Stack spacing={1}>
                <Typography
                  sx={() => ({
                    fontSize: "14px",
                    flex: 0,
                    whiteSpace: "nowrap",
                  })}
                >
                  #{qIdx + 1}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={question.text}
                    onChange={(e) =>
                      updateQuestion(qIdx, "text", e.target.value)
                    }
                    placeholder={t(
                      Resources.Modules.Questions.QuestionTextPlaceholder,
                    )}
                    error={!!qErrors?.text}
                    helperText={qErrors?.text}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeQuestion(qIdx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Select
                    size="small"
                    value={question.type}
                    onChange={(e) =>
                      updateQuestion(
                        qIdx,
                        "type",
                        e.target.value as ModuleQuestionType,
                      )
                    }
                    startAdornment={
                      questionTypeIconMap[question.type] ? (
                        <InputAdornment position="start">
                          <Box
                            sx={(theme) => ({
                              display: "flex",
                              color: theme.palette.primary.main,
                            })}
                          >
                            {questionTypeIconMap[question.type]}
                          </Box>
                        </InputAdornment>
                      ) : undefined
                    }
                    sx={{ minWidth: 220 }}
                  >
                    {QUESTION_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(Resources.Modules.Questions.Types[type])}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={question.isRequired}
                        onChange={(e) =>
                          updateQuestion(qIdx, "isRequired", e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={t(Resources.Modules.Questions.Required)}
                    slotProps={{
                      typography: { variant: "body2" },
                    }}
                  />
                </Stack>

                {isChoiceType(question.type) && (
                  <Box sx={{ pl: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      mb={0.5}
                      display="block"
                    >
                      {t(Resources.Modules.Questions.Options)}
                    </Typography>
                    <Stack spacing={0.5}>
                      {question.options.map((option, oIdx) => (
                        <Stack key={option._key} direction="row" spacing={1}>
                          <TextField
                            fullWidth
                            size="small"
                            value={option.text}
                            onChange={(e) =>
                              updateOptionText(qIdx, oIdx, e.target.value)
                            }
                            placeholder={t(
                              Resources.Modules.Questions.OptionTextPlaceholder,
                            )}
                            error={!!qErrors?.optionTexts?.[oIdx]}
                            helperText={qErrors?.optionTexts?.[oIdx]}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeOption(qIdx, oIdx)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}

                      {isWithOtherType(question.type) && (
                        <TextField
                          fullWidth
                          size="small"
                          disabled
                          value={t(
                            Resources.Modules.Questions.OtherOptionLabel,
                          )}
                          sx={(theme) => ({
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: theme.palette.text.secondary,
                              fontStyle: "italic",
                            },
                          })}
                        />
                      )}

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addOption(qIdx)}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          {t(Resources.Modules.Questions.AddOption)}
                        </Button>
                        {qErrors?.options && (
                          <FormHelperText error>
                            {qErrors.options}
                          </FormHelperText>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addQuestion}
          fullWidth
        >
          {t(Resources.Modules.Questions.AddQuestion)}
        </Button>
      </Stack>
    </BlModal>
  );
};

const QUESTION_TYPE_OPTIONS = [
  ModuleQuestionType.Text,
  ModuleQuestionType.SingleChoice,
  ModuleQuestionType.SingleChoiceWithOther,
  ModuleQuestionType.MultiChoice,
  ModuleQuestionType.MultiChoiceWithOther,
] as const;

const isChoiceType = (type: ModuleQuestionType): boolean =>
  type !== ModuleQuestionType.Text;

const isWithOtherType = (type: ModuleQuestionType): boolean =>
  [
    ModuleQuestionType.SingleChoiceWithOther,
    ModuleQuestionType.MultiChoiceWithOther,
  ].includes(type);

const questionTypeIconMap: Record<ModuleQuestionType, React.ReactNode> = {
  [ModuleQuestionType.Text]: <EditNoteOutlinedIcon fontSize="small" />,
  [ModuleQuestionType.SingleChoice]: (
    <RadioButtonCheckedOutlinedIcon fontSize="small" />
  ),
  [ModuleQuestionType.SingleChoiceWithOther]: (
    <RadioButtonCheckedOutlinedIcon fontSize="small" />
  ),
  [ModuleQuestionType.MultiChoice]: <CheckBoxOutlinedIcon fontSize="small" />,
  [ModuleQuestionType.MultiChoiceWithOther]: (
    <CheckBoxOutlinedIcon fontSize="small" />
  ),
};
